import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import screenSizeObserver from '../utils/screenSizeObserver'
import useWindowDimensions from '../utils/useWindowDimensions'

// the values double as the translation keys of the messages
export type ErrorMessageType =
  | 'APIErrorMessage'
  | 'resolutionErrorMessage'
  | 'windowSizeErrorMessage'
  | 'screenOrientationErrorMessage'

interface ContextType {
  isInitialLoading: boolean
  setIsInitialLoading: Dispatch<SetStateAction<boolean>>
  isErrorMessageVisible: boolean
  errorMessageText: string
  isReloadOnError: boolean
  showErrorMessage: (errorMessage: ErrorMessageType) => void
  hideErrorMessage: () => void
  rotateDeviceNotificationVisible: boolean
}

interface Props {
  children: ReactNode
}

const Context = createContext({} as ContextType)

// Loading and the blocking messages that take over the whole screen
const AppStatusContext = ({ children }: Props) => {
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(false)
  const [isErrorMessageVisible, setIsErrorMessageVisible] = useState<boolean>(false)
  const [errorMessageText, setErrorMessageText] = useState<string>('')
  const [isReloadOnError, setIsReloadOnError] = useState<boolean>(false)
  const [rotateDeviceNotificationVisible, setRotateDeviceNotificationVisible] =
    useState<boolean>(false)
  const { windowWidth, windowHeight } = useWindowDimensions()

  const showErrorMessage = (errorMessage: ErrorMessageType) => {
    setIsErrorMessageVisible(true)
    setErrorMessageText(errorMessage)
    // only a failed request can be fixed by reloading, the screen size errors fix themselves
    setIsReloadOnError(errorMessage === 'APIErrorMessage')
  }

  const hideErrorMessage = () => {
    setIsErrorMessageVisible(false)
    setErrorMessageText('')
  }

  useEffect(() => {
    screenSizeObserver(
      windowWidth,
      windowHeight,
      showErrorMessage,
      hideErrorMessage,
      setRotateDeviceNotificationVisible,
    )
  }, [windowWidth, windowHeight])

  return (
    <Context.Provider
      value={{
        isInitialLoading,
        setIsInitialLoading,
        isErrorMessageVisible,
        errorMessageText,
        isReloadOnError,
        showErrorMessage,
        hideErrorMessage,
        rotateDeviceNotificationVisible,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useContextAppStatus = () => {
  return useContext(Context)
}

export default AppStatusContext
